import type { Barber, Booking, Business } from './validation.ts';
import { ACTIVE_STATUSES } from './validation.ts';
import { DAY, HOUR, MINUTE, addDays, dateKeyInTz, parseHours, weekdayOf, zonedToUtc, type DateKey } from './time.ts';

export interface Slot {
  start: Date;
  end: Date;
  /** Barbers free for the whole slot, in config order. */
  barberIds: string[];
}

export interface SlotQuery {
  business: Business;
  date: DateKey;
  serviceId: string;
  /** A barber id, or "any". */
  barberId: string;
  bookings: readonly Pick<Booking, 'barber_id' | 'starts_at' | 'ends_at' | 'status'>[];
  now: Date;
}

export function barbersForService(business: Business, serviceId: string): Barber[] {
  return business.barbers.filter((b) => b.services.includes(serviceId));
}

/** First and last bookable instants according to the tenant's rules. */
export function bookingWindow(business: Business, now: Date): { from: Date; to: Date } {
  return {
    from: new Date(now.getTime() + business.rules.min_advance_booking_hours * HOUR),
    to: new Date(now.getTime() + business.rules.max_advance_booking_days * DAY),
  };
}

/** Bookable dates (tenant timezone) from today until the max-advance limit. */
export function bookableDates(business: Business, now: Date): DateKey[] {
  const tz = business.timezone;
  const { to } = bookingWindow(business, now);
  const last = dateKeyInTz(to, tz);
  const dates: DateKey[] = [];
  for (let d = dateKeyInTz(now, tz); d <= last; d = addDays(d, 1)) {
    if (isOpen(business, d)) dates.push(d);
  }
  return dates;
}

export function isOpen(business: Business, date: DateKey): boolean {
  if (business.closed_dates.includes(date)) return false;
  return parseHours(business.business_hours[weekdayOf(date)]) !== null;
}

/**
 * All start times on `date` where the service fits completely inside opening
 * hours and at least one eligible barber has no overlapping active booking.
 */
export function availableSlots(q: SlotQuery): Slot[] {
  const { business, date } = q;
  const tz = business.timezone;
  const service = business.services.find((s) => s.id === q.serviceId);
  if (!service || !isOpen(business, date)) return [];
  const hours = parseHours(business.business_hours[weekdayOf(date)]);
  if (!hours) return [];

  const eligible = barbersForService(business, service.id).filter((b) => q.barberId === 'any' || b.id === q.barberId);
  if (eligible.length === 0) return [];

  const active = q.bookings.filter((b) => ACTIVE_STATUSES.includes(b.status));
  const sameDay = active.filter((b) => dateKeyInTz(new Date(b.starts_at), tz) === date);
  if (sameDay.length >= business.rules.max_clients_per_day) return [];

  const busy = new Map<string, Array<[number, number]>>();
  for (const b of active) {
    const list = busy.get(b.barber_id) ?? [];
    list.push([Date.parse(b.starts_at), Date.parse(b.ends_at)]);
    busy.set(b.barber_id, list);
  }

  const window = bookingWindow(business, q.now);
  const [open, close] = hours;
  const step = business.rules.slot_step_min;
  const slots: Slot[] = [];

  for (let t = open; t + service.duration_min <= close; t += step) {
    const start = zonedToUtc(date, t, tz);
    const end = new Date(start.getTime() + service.duration_min * MINUTE);
    if (start < window.from || start > window.to) continue;
    const s = start.getTime();
    const e = end.getTime();
    const free = eligible.filter((barber) => !(busy.get(barber.id) ?? []).some(([bs, be]) => bs < e && s < be));
    if (free.length > 0) slots.push({ start, end, barberIds: free.map((b) => b.id) });
  }
  return slots;
}

/** Picks the barber with the fewest bookings that day, to spread the load. */
export function pickBarber(slot: Slot, bookings: readonly Pick<Booking, 'barber_id' | 'starts_at'>[], date: DateKey, tz: string): string {
  const load = (id: string) => bookings.filter((b) => b.barber_id === id && dateKeyInTz(new Date(b.starts_at), tz) === date).length;
  return [...slot.barberIds].sort((a, b) => load(a) - load(b))[0]!;
}

/** Whether a client may still cancel this booking themselves. */
export function canClientCancel(business: Business, booking: Pick<Booking, 'starts_at' | 'status'>, now: Date): boolean {
  if (!ACTIVE_STATUSES.includes(booking.status)) return false;
  return Date.parse(booking.starts_at) - now.getTime() >= business.rules.cancellation_hours * HOUR;
}
